// React
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Material UI
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string) => EMAIL_REGEX.test(email.trim());

const NewsletterDialog = ({
  open,
  defaultEmail,
  onConfirm,
  onClose,
}: {
  open: boolean;
  defaultEmail: string | undefined;
  onConfirm: (email: string) => Promise<void>;
  onClose: () => void;
}) => {
  const [emailInput, setEmailInput] = useState(defaultEmail || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    setEmailInput(defaultEmail || '');
    setTouched(false);
  }, [defaultEmail]);

  // Reset touched state when the dialog opens so stale errors don't show on re-open.
  useEffect(() => {
    if (open) {
      setTouched(false);
    }
  }, [open]);

  const emailError = touched && !isValidEmail(emailInput);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    if (!isValidEmail(emailInput)) {
      setTouched(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm(emailInput.trim());
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Backdrop open={open}>
      <Dialog maxWidth={'md'} open={open}>
        <form noValidate onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <DialogTitle sx={{ maxWidth: '80%' }}>{t('NEWSLETTER.DIALOG.TITLE')}</DialogTitle>
            <IconButton disabled={isSubmitting} onClick={() => onClose()} sx={{ alignItems: 'flex-start', height: 'min-content' }}>
              <ClearIcon />
            </IconButton>
          </Box>
          <DialogContent>
            <Typography>{t('NEWSLETTER.DIALOG.DESCRIPTION')}</Typography>
            <br />
            <Typography>{t('NEWSLETTER.DIALOG.CALLTOACTION')}</Typography>
            <TextField
              disabled={isSubmitting}
              error={emailError}
              helperText={emailError ? t('NEWSLETTER.DIALOG.INVALID_EMAIL') : undefined}
              label={t('NEWSLETTER.DIALOG.TEXTFIELDLABEL')}
              onBlur={() => setTouched(true)}
              onChange={(e) => setEmailInput(e.target.value)}
              sx={{ width: '90%', ml: '5%', mr: '5%', mt: '5px' }}
              type='email'
              value={emailInput}
              variant={'outlined'}
            />
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'center', pt: 0 }}>
            <Button
              color='primary'
              disabled={!emailInput.trim() || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress color='inherit' size={16} /> : undefined}
              type='submit'
              variant='contained'>
              {t('COMMON.CONFIRM')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Backdrop>
  );
};

export default NewsletterDialog;
